import { useEffect, useState } from 'react';
import CandlestickChart from '@/components/feature/CandlestickChart';
import type { CandlestickData, Time } from 'lightweight-charts';

function generateMockCandles(basePrice: number, count = 60): CandlestickData<Time>[] {
  const data: CandlestickData<Time>[] = [];
  let price = basePrice;
  const now = new Date();
  // Round down to nearest 15 min
  const start = new Date(now);
  start.setMinutes(Math.floor(start.getMinutes() / 15) * 15, 0, 0);

  for (let i = count; i >= 0; i--) {
    const t = new Date(start);
    t.setMinutes(t.getMinutes() - i * 15);
    const timestamp = Math.floor(t.getTime() / 1000) as Time;
    const range = basePrice * 0.004;
    const open = price;
    const close = price + (Math.random() - 0.5) * range;
    const high = Math.max(open, close) + Math.random() * range * 0.35;
    const low = Math.min(open, close) - Math.random() * range * 0.35;
    data.push({
      time: timestamp,
      open: Number(open.toFixed(2)),
      high: Number(high.toFixed(2)),
      low: Number(low.toFixed(2)),
      close: Number(close.toFixed(2)),
    });
    price = close;
  }
  return data;
}

interface Props {
  symbol?: string;
  basePrice?: number;
}

export default function TradingChart({ symbol, basePrice = 2500 }: Props) {
  const [data, setData] = useState<CandlestickData<Time>[]>(() =>
    generateMockCandles(basePrice, 60),
  );

  // Reset candles when switching instrument
  useEffect(() => {
    setData(generateMockCandles(basePrice, 60));
  }, [symbol, basePrice]);

  // Live-tick the last candle every second
  useEffect(() => {
    const id = setInterval(() => {
      setData((prev) => {
        const next = [...prev];
        const last = { ...next[next.length - 1] };
        const range = basePrice * 0.0015;
        const close = last.close + (Math.random() - 0.5) * range;
        last.close = Number(close.toFixed(2));
        last.high = Math.max(last.high, last.close);
        last.low = Math.min(last.low, last.close);
        next[next.length - 1] = last;
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [symbol, basePrice]);

  return (
    <div className="rounded-xl border border-background-200/60 p-4 md:p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground-950">
            Biểu đồ nến {symbol || ''}
          </h3>
          <p className="text-xs text-foreground-600">
            Khung 15 phút
            <span className="text-secondary-700 ml-1">· Cập nhật từng giây</span>
          </p>
        </div>
        <span className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium whitespace-nowrap">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          Live
        </span>
      </div>
      <CandlestickChart data={data} height={320} />
    </div>
  );
}